import { useValidators } from "@/composables/useValidators";
import type { PolishDeviceMetadata } from "@/dashboard/production_strategy/issuance-reporting/pl/PolishPoAConstants";
import type { Company } from "@/types";
import useVuelidate from "@vuelidate/core";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Composable for Polish device validation and form management.
 * Handles Polish-specific form validation, state management, and reset functionality.
 */
export function usePolishDeviceValidation(company: Company) {
  const { t } = useI18n();
  const validators = useValidators(t);

  // Polish device metadata form state
  const polishForm = ref<PolishDeviceMetadata>({
    installation_address: "",
    metering_system_number: "",
    metering_system_multiplier: 1,
    osd_name: "",
    osd_address: "",
  });

  // Polish form validation rules (only for Polish companies)
  const polishRules = computed(() => {
    if (company.country !== "PL") {
      return {};
    }
    return {
      installation_address: {
        required: validators.required,
        maxLength: validators.maxLength(500),
      },
      metering_system_number: {
        required: validators.required,
        maxLength: validators.maxLength(100),
      },
      metering_system_multiplier: {
        required: validators.required,
        minValue: validators.min(1),
      },
      osd_name: {
        required: validators.required,
        maxLength: validators.maxLength(200),
      },
      osd_address: {
        required: validators.required,
        maxLength: validators.maxLength(500),
      },
    };
  });

  // Vuelidate validation instance for Polish form
  const polishV$ = useVuelidate(polishRules, polishForm);

  /**
   * Validate Polish form fields if this is a Polish company
   */
  const validatePolishForm = async (): Promise<boolean> => {
    if (company.country !== "PL") {
      return true;
    }

    const isPolishFormValid = await polishV$.value.$validate();
    if (!isPolishFormValid) {
      polishV$.value.$touch();
    }
    return isPolishFormValid;
  };

  /**
   * Reset Polish form to initial state
   */
  const resetPolishForm = () => {
    polishForm.value = {
      installation_address: "",
      metering_system_number: "",
      metering_system_multiplier: 1,
      osd_name: "",
      osd_address: "",
    };
    polishV$.value.$reset();
  };

  return {
    polishForm,
    polishV$,
    validatePolishForm,
    resetPolishForm,
  };
}
